import { useState } from "react";
import { useAllUserSubscriptions } from "@/hooks/useAllUserSubscriptions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, Calendar, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export const AllSubscriptionsManager = () => {
  const { subscriptions, loading, error } = useAllUserSubscriptions();
  const [managingExam, setManagingExam] = useState<string | null>(null);

  const handleManageSubscription = async (examSlug: string) => {
    try {
      setManagingExam(examSlug);
      const { data, error } = await supabase.functions.invoke("create-customer-portal", {
        body: { returnUrl: window.location.href },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Error creating customer portal session:", err);
      toast.error("Failed to open subscription management portal");
    } finally {
      setManagingExam(null);
    }
  };

  const getPlanName = (subscriptionType: string | null): string => {
    if (!subscriptionType) return "Free Trial";
    const planMap: Record<string, string> = {
      monthly: "Monthly Plan",
      quarterly: "Quarterly Plan", 
      biannual: "Biannual Plan",
      annual: "Annual Plan",
    };
    return planMap[subscriptionType] || subscriptionType;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load subscriptions. Please try again later.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            My Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <ShieldCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No active subscriptions</p>
            <p className="text-sm text-muted-foreground mt-2">
              Subscribe to an exam to get started
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          My Subscriptions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          {subscriptions.map((subscription) => (
            <Card key={subscription.examSlug} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">
                      {subscription.examIcon && <span className="mr-2">{subscription.examIcon}</span>}
                      {subscription.examName}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {getPlanName(subscription.subscriptionType)}
                    </p>
                  </div>
                  <Badge
                    variant={
                      subscription.status === 'trial'
                        ? 'secondary'
                        : subscription.status === 'active'
                        ? 'default'
                        : 'destructive'
                    }
                  >
                    {subscription.status === 'trial' && 'Trial'}
                    {subscription.status === 'active' && 'Active'}
                    {subscription.status === 'expired' && 'Expired'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {subscription.daysRemaining !== null && subscription.daysRemaining <= 7 && !subscription.isExpired && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {subscription.daysRemaining} day{subscription.daysRemaining !== 1 ? 's' : ''} remaining
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  {subscription.isInTrial && subscription.trialExpiresAt && (
                    <span className="text-muted-foreground">
                      Trial ends {formatDistanceToNow(new Date(subscription.trialExpiresAt), { addSuffix: true })}
                    </span>
                  )}
                  {subscription.status === 'active' && subscription.expiresAt && (
                    <span className="text-muted-foreground">
                      Next billing {formatDistanceToNow(new Date(subscription.expiresAt), { addSuffix: true })}
                    </span>
                  )}
                  {subscription.isExpired && (
                    <span className="text-destructive">
                      Subscription expired
                    </span>
                  )}
                </div>

                {subscription.hasStripeCustomer && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleManageSubscription(subscription.examSlug)}
                    disabled={managingExam === subscription.examSlug}
                  >
                    {managingExam === subscription.examSlug ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Opening Portal...
                      </>
                    ) : (
                      'Manage Subscription'
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
